import React, { useState, useEffect } from 'react';
import axios from '../../config/axios';

const ProductForm = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    price: '',
    description: '',
    range: '',
    top_speed: '',
    color: '',
    is_active: true,
    image: null
  });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        model: product.model || '',
        price: product.price || '',
        description: product.description || '',
        range: product.range || '',
        top_speed: product.top_speed || '',
        color: product.color || '',
        is_active: product.is_active !== undefined ? product.is_active : true,
        image: null
      });
      // Set preview from existing image
      if (product.image_url) {
        const imageUrl = product.image_url.startsWith('http') 
          ? product.image_url 
          : `https://janvaahan.com/backend/public${product.image_url}`;
        setPreview(imageUrl);
      }
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match('image.*')) {
        setError('Please select an image file (JPEG, PNG, etc.)');
        return;
      }
      
      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2MB');
        return;
      }
      
      setFormData({ ...formData, image: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add all fields
      if (formData.name) submitData.append('name', formData.name);
      if (formData.model) submitData.append('model', formData.model);
      if (formData.price) submitData.append('price', formData.price);
      if (formData.description) submitData.append('description', formData.description);
      if (formData.range) submitData.append('range', formData.range);
      if (formData.top_speed) submitData.append('top_speed', formData.top_speed);
      if (formData.color) submitData.append('color', formData.color);
      submitData.append('is_active', formData.is_active ? '1' : '0');
      
      // Add image if new image selected
      if (formData.image) {
        submitData.append('image', formData.image);
      }
      
      let response;
      if (product) {
        // Update existing product
        // For PUT request with FormData, we need to use POST with _method
        submitData.append('_method', 'PUT');
        response = await axios.post(`/admin/products/${product.id}`, submitData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        });
        console.log('Update response:', response.data);
      } else {
        // Create new product
        response = await axios.post('/admin/products', submitData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          }
        });
        console.log('Create response:', response.data);
      }
      
      if (response.data.message || response.data.product) {
        alert(product ? 'Product updated successfully!' : 'Product created successfully!');
        onSave(); // Callback to refresh product list
        onClose(); // Close the form
      } else {
        throw new Error('Invalid response from server');
      }
      
    } catch (error) {
      console.error('Error saving product:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.data?.errors) {
        // Handle validation errors
        const validationErrors = Object.values(error.response.data.errors).flat();
        setError(validationErrors.join(', '));
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError(error.message || 'Error saving product. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">Product Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="e.g., Janvahan JV1"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Model *</label>
              <input
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="e.g., JV1"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Price *</label>
              <input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="e.g., From ₹99,999*"
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                required
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Detailed description of the product..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Range (km)</label>
              <input
                type="number"
                name="range"
                value={formData.range}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="e.g., 80"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Top Speed (km/h)</label>
              <input
                type="number"
                name="top_speed"
                value={formData.top_speed}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="e.g., 75"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="e.g., Red, Black, Blue"
              />
            </div>
            
            <div className="flex items-center mt-6">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="mr-2 h-4 w-4 text-red-600"
              />
              <label className="text-sm font-medium">Active (visible on website)</label>
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">
                Product Image {!product && '*'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full"
                required={!product && !formData.image}
              />
              {preview && (
                <div className="mt-2">
                  <img src={preview} alt="Preview" className="h-32 object-cover rounded" />
                  <p className="text-xs text-gray-500 mt-1">Preview</p>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Recommended: JPG, PNG format, max 2MB
              </p>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition"
            >
              {loading ? (product ? 'Updating...' : 'Creating...') : (product ? 'Update Product' : 'Create Product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;